<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { describePhase, type Phase } from '../lib/phase'

const props = defineProps<{
  /** Current phase, or null when idle. */
  phase: Phase | null
  /** Epoch ms when the phase began. */
  since: number
  attempt?: number
  /** Epoch ms of the next retry, for the `retrying` countdown. */
  retryAt?: number | null
  /** Keep the block's height when idle so the layout does not jump. */
  reserve?: boolean
}>()

// One 1 s timer, alive only while a phase is shown; the DOM changes at most once a second.
const now = ref(Date.now())
let timer: number | null = null

function stopTimer(): void {
  if (timer !== null) window.clearInterval(timer)
  timer = null
}

watch(
  () => props.phase,
  (phase) => {
    stopTimer()
    if (phase === null) return
    now.value = Date.now()
    timer = window.setInterval(() => {
      now.value = Date.now()
    }, 1000)
  },
  { immediate: true },
)

onBeforeUnmount(stopTimer)

const view = computed(() => {
  if (props.phase === null) return null
  return describePhase({
    phase: props.phase,
    elapsedSeconds: (now.value - props.since) / 1000,
    attempt: props.attempt,
    retryInSeconds: props.retryAt ? (props.retryAt - now.value) / 1000 : 0,
  })
})
</script>

<template>
  <div
    class="phase-status"
    :class="[view ? `phase-status--${view.tone}` : 'phase-status--idle', { 'phase-status--reserve': reserve }]"
    role="status"
    aria-live="polite"
  >
    <template v-if="view">
      <p class="phase-status__main">
        <span class="phase-status__dot" aria-hidden="true" />
        <span>{{ view.text }}</span>
        <span v-if="view.elapsedLabel" class="phase-status__elapsed">{{ view.elapsedLabel }}</span>
      </p>
      <p v-if="view.reassurance" class="phase-status__note">{{ view.reassurance }}</p>
      <p v-if="view.safety" class="phase-status__note">{{ view.safety }}</p>
    </template>
  </div>
</template>

<style scoped>
.phase-status {
  font-size: 0.85rem;
  color: var(--ink-soft);
}

.phase-status--reserve {
  min-height: 5.2em;
}

.phase-status p {
  margin: 0 0 4px;
}

.phase-status__main {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.phase-status__dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: phase-pulse 1.4s ease-in-out infinite;
}

.phase-status__elapsed {
  margin-left: auto;
  font-weight: 400;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

.phase-status__note {
  color: var(--muted);
}

.phase-status--warn {
  color: #734308;
}

@keyframes phase-pulse {
  50% {
    opacity: 0.3;
  }
}

@media (prefers-reduced-motion: reduce) {
  .phase-status__dot {
    animation: none;
  }
}
</style>
